/**
 * Node Configuration Sidebar - Edit task parameters
 */

import React, { useCallback, useEffect, useState } from 'react';
import type { PipelineNode } from '../types/pipeline';
import { StepCardinality } from '../types/pipeline';
import type { AgentConfig } from '../types/agent';
import { apiClient } from '../api/client';
import { groupAgentsBySlug } from '../utils/agentVersions';
import { PencilIcon, PlayIcon } from './icons';
import './NodeConfigSidebar.css';

interface NodeConfigSidebarProps {
  node: PipelineNode | null;
  onSave: (
    config: Record<string, any>,
    agentId?: string,
    label?: string,
    agentVersion?: number
  ) => void;
  onClose: () => void;
  taskSchema?: any;
  onOpenAgentPanel?: (opts: {
    mode: 'library' | 'edit' | 'sandbox';
    agentId?: string;
    onPick?: (agentId: string) => void;
    onAgentUpdated?: (agentId: string) => void;
    onClose?: () => void;
  }) => void;
}

const SIDEBAR_WIDTH_STORAGE_KEY = 'raiqon_node_config_sidebar_width';
const DEFAULT_SIDEBAR_WIDTH = 400;
const MIN_SIDEBAR_WIDTH = 320;
const MAX_SIDEBAR_WIDTH = 900;

const loadPersistedWidth = (): number => {
  try {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    const width = saved ? parseInt(saved, 10) : NaN;
    if (!isNaN(width)) {
      return Math.min(Math.max(width, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH);
    }
  } catch (e) {
    console.error('Failed to load sidebar width:', e);
  }
  return DEFAULT_SIDEBAR_WIDTH;
};

export const NodeConfigSidebar: React.FC<NodeConfigSidebarProps> = ({
  node,
  onSave,
  onClose,
  onOpenAgentPanel,
}) => {
  const [nodeLabel, setNodeLabel] = useState<string>('');
  const [sidebarWidth, setSidebarWidth] = useState<number>(loadPersistedWidth);

  // Drag-to-resize: the handle sits on the panel's left edge, dragging left grows it
  // (panel is right-anchored via CSS, same convention as ExecutionMonitor's detail panel).
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      setSidebarWidth(Math.min(Math.max(startWidth + delta, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH));
    };

    const handleMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      setSidebarWidth((width) => {
        try {
          localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(width));
        } catch (e) {
          console.error('Failed to save sidebar width:', e);
        }
        return width;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [sidebarWidth]);
  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>(undefined);
  // Pinned version for the selected agent (undefined = float to latest active version).
  const [selectedAgentVersion, setSelectedAgentVersion] = useState<number | undefined>(undefined);
  const [availableAgents, setAvailableAgents] = useState<AgentConfig[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [cardinality, setCardinality] = useState<StepCardinality>(StepCardinality.PER_ITEM);

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const agents = await apiClient.listAgents();
      setAvailableAgents(agents);
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoadingAgents(false);
    }
  };

  // Load available agents
  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    if (node) {
      console.log('NodeConfigSidebar received node:', node);
      setNodeLabel(node.label || '');
      setSelectedAgentId(node.agent_id);
      setSelectedAgentVersion(node.agent_version);
      setCardinality(node.config?.cardinality || StepCardinality.PER_ITEM);
    }
  }, [node]);

  if (!node) return null;

  const handleSave = () => {
    const config: Record<string, any> = { ...node.config, cardinality };
    onSave(config, selectedAgentId, nodeLabel, selectedAgentVersion);
    onClose();
  };

  const selectedAgent = availableAgents.find(a => a.id === selectedAgentId);
  const selectedAgentGroup = selectedAgent
    ? groupAgentsBySlug(availableAgents).find(g => g.key === (selectedAgent.slug || selectedAgent.id))
    : undefined;

  // For a specific task-type node, only offer agents built for that task type
  // (its seeded default plus any matching custom agents). The generic "agent"
  // node has no fixed task type, so it can pick from the full library.
  const filteredAgents = node.task_type === 'agent'
    ? availableAgents
    : availableAgents.filter(a => a.task_type === node.task_type);

  // A task-type-specific node always runs that task type's built-in default agent -
  // hardcoded model and prompt, no dynamic component. Only the generic "agent" node
  // (no fixed task type) lets the user pick a real, user-configured agent.
  const isLockedToDefault = node.task_type !== 'agent';
  const defaultAgentForTaskType = filteredAgents.find(a => a.is_default);
  const displayAgent = selectedAgent || defaultAgentForTaskType;

  return (
    <div className="node-config-sidebar" style={{ width: sidebarWidth }}>
      <div className="node-config-sidebar-resizer" onMouseDown={handleResizeStart} />
      <div className="sidebar-header">
        <div>
          <h3>Configure Node</h3>
          <span className="text-xs text-secondary">{node.id}</span>
        </div>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="sidebar-body">
        <div className="config-section">
          <h4>Node Label</h4>
          <div className="config-field">
            <label className="config-label">
              Display Name
              <span className="text-xs text-secondary ml-2">(optional)</span>
            </label>
            <p className="config-description">
              Human-readable label for this node in the workflow
            </p>
            <input
              type="text"
              value={nodeLabel}
              onChange={(e) => setNodeLabel(e.target.value)}
              placeholder={node.id}
              className="config-input"
            />
          </div>
        </div>

        <div className="config-section">
          <h4>Agent</h4>
          {isLockedToDefault ? (
            <div className="config-field">
              <p className="config-description">
                This node runs the built-in default agent for the "{node.task_type}" task
                type - its model and prompt are fixed by the server configuration and can't
                be changed here. Add a generic "Agent" node instead if you need a custom,
                user-configured agent for this task.
              </p>
              {displayAgent && (
                <div className="agent-preview">
                  <div className="agent-preview-header">
                    <strong>{displayAgent.name}</strong>
                    <span className="agent-badge">Default</span>
                  </div>
                  {displayAgent.description && (
                    <p className="agent-preview-description">{displayAgent.description}</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="config-field">
              <label className="config-label">
                Agent
              </label>
              <p className="config-description">
                The agent defines the task type, context inputs, output format, and model configuration.
                Browse the agent library to select or create an agent for this node.
              </p>
              {onOpenAgentPanel && (
                <button
                  type="button"
                  className="button-secondary"
                  disabled={loadingAgents}
                  onClick={() =>
                    onOpenAgentPanel({
                      mode: 'library',
                      agentId: selectedAgentId,
                      onPick: (agentId) => {
                        setSelectedAgentId(agentId);
                        // A freshly-picked agent floats to latest until pinned below.
                        setSelectedAgentVersion(undefined);
                      },
                      onClose: loadAgents,
                    })
                  }
                >
                  {selectedAgent ? 'Change Agent' : 'Browse / Create Agent'}
                </button>
              )}
              {selectedAgent && (
                <div className="agent-preview">
                  <div className="agent-preview-header">
                    <strong>{selectedAgent.name}</strong>
                    <span className="agent-preview-header-right">
                      <span className="agent-badge">{selectedAgent.category || 'Custom'}</span>
                      {onOpenAgentPanel && (
                        <span className="agent-preview-actions">
                          <button
                            type="button"
                            className="agent-preview-action-button"
                            title="Edit agent"
                            onClick={() =>
                              onOpenAgentPanel({
                                mode: 'edit',
                                agentId: selectedAgent.id,
                                onAgentUpdated: (agentId) => {
                                  setSelectedAgentId(agentId);
                                  // The edit minted a new version row - follow it and
                                  // float to latest rather than keep pointing at the
                                  // pre-edit id/version (see create_agent_version).
                                  setSelectedAgentVersion(undefined);
                                  // Refresh now, not just on close: availableAgents
                                  // won't contain this new id yet, so without this
                                  // `selectedAgent` below resolves to undefined and
                                  // the whole preview (incl. these Edit/Test buttons)
                                  // disappears until the panel is closed and reopened.
                                  loadAgents();
                                },
                                onClose: loadAgents,
                              })
                            }
                          >
                            <PencilIcon /> Edit
                          </button>
                          <button
                            type="button"
                            className="agent-preview-action-button"
                            title="Test agent"
                            onClick={() =>
                              onOpenAgentPanel({ mode: 'sandbox', agentId: selectedAgent.id, onClose: loadAgents })
                            }
                          >
                            <PlayIcon /> Test
                          </button>
                        </span>
                      )}
                    </span>
                  </div>
                  <p className="agent-preview-description">{selectedAgent.description}</p>
                  <div className="agent-preview-meta">
                    <span className="meta-item">
                      <strong>Task:</strong> {node.task_type}
                    </span>
                    {selectedAgent.model && (
                      <>
                        <span className="meta-item">
                          <strong>Model:</strong> {selectedAgent.model.provider} / {selectedAgent.model.model_name}
                        </span>
                        <span className="meta-item">
                          <strong>Temperature:</strong> {selectedAgent.model.temperature}
                        </span>
                      </>
                    )}
                  </div>
                  {selectedAgentGroup && selectedAgentGroup.versions.length > 1 && (
                    <div className="config-field">
                      <label className="config-label">Version</label>
                      <p className="config-description">
                        Pin this step to a specific saved version, or float to whatever is
                        currently the latest.
                      </p>
                      <select
                        value={selectedAgentVersion === undefined ? '' : selectedAgentId}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value) {
                            // Leave agent_id pointed at whichever version row is currently
                            // displayed - with agent_version unset, the backend re-resolves
                            // it to the slug's latest active version regardless (see
                            // _load_agent_by_id in dynamic_agent.py).
                            setSelectedAgentVersion(undefined);
                            return;
                          }
                          const version = selectedAgentGroup.versions.find(v => v.id === value);
                          setSelectedAgentId(version?.id);
                          setSelectedAgentVersion(version ? parseInt(version.version, 10) || undefined : undefined);
                        }}
                        className="config-input"
                      >
                        <option value="">Latest</option>
                        {selectedAgentGroup.versions.map(v => (
                          <option key={v.id} value={v.id}>
                            v{v.version}{v.id === selectedAgentGroup.versions[0].id ? ' (latest)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="config-section">
          <h4>Execution Scope</h4>
          <div className="config-field">
            <label className="config-label">
              Runs
            </label>
            <p className="config-description">
              Once per item runs this step for every item the workflow iterates over - its agent can
              still read all items of an input or a tracker's schema next to the current item. Once
              over all items runs it a single time for the whole batch, e.g. to summarize a tracker;
              Single item variables then list every item.
            </p>
            <select
              value={cardinality}
              onChange={(e) => setCardinality(e.target.value as StepCardinality)}
              className="config-input"
            >
              <option value={StepCardinality.PER_ITEM}>Once per item (default)</option>
              <option value={StepCardinality.AGGREGATE}>Once over all items</option>
            </select>
          </div>
        </div>
      </div>

      <div className="sidebar-footer">
        <button className="button-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="button-primary" onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  );
};
